import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import { ChevronLeftIcon, ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getDeviceInfo } from '../utils/deviceInfo';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_API_URL;

const LiveTransaction = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [access_data, setAccess_data] = useState([]);
  const refreshIntervalRef = useRef(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);

  useEffect(() => {
    if (sessionCheck() == null) {
      navigate("../login", { replace: true });
    } else {
      setAccess_data(JSON.parse(sessionCheck()));
    }

    // Clean up interval on component unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (access_data?.username) {
      fetchTransactions();

      // Set up interval for fetching transactions every 5 seconds
      refreshIntervalRef.current = setInterval(() => {
        fetchTransactions();
      }, 5000);
    }
  }, [access_data?.username]);

  const fetchTransactions = async () => {
    try {
      const dataToEncrypt = {
        user_id: access_data?.username || '',
        login_id: access_data?.login_id || '',
        device_id: getDeviceInfo().deviceId,
        payload: {
          query: 'live-transactions',
          limit: 10
        }
      };

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);

      if (!encryptedMessage) {
        toast.error("Encryption failed. Please try again.");
        return;
      }

      const payloadToSend = {
        encrypted_key: encryptedMessage.encryptedKey,
        encrypted_data: encryptedMessage.encryptedData,
      };

      const response = await fetch(`${API_URL}order-list-live.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify(payloadToSend),
      });

      const result = await response.json();

      if (result.status === true) {
        // Update transactions without affecting UI
        setTransactions(prevTransactions => {
          // Check if there are new transactions
          const newTransactions = result.data.filter(newTxn =>
            !prevTransactions.some(prevTxn => prevTxn.order_id === newTxn.order_id)
          );

          // Show notification for new transactions
          if (newTransactions.length > 0 && prevTransactions.length > 0) {
            toast.info(`${newTransactions.length} new transaction(s) found`, {
              autoClose: 3000,
              hideProgressBar: true,
            });
          }

          // Return the latest transactions (always 10 as per API)
          return result.data;
        });

        setLastUpdateTime(new Date());
      } else {
        console.error("Failed to fetch transactions:", result.message);
      }
    } catch (error) {
      console.error("Fetch transactions error:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatTimeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
    return `${Math.floor(diffInSeconds / 3600)} hour${Math.floor(diffInSeconds / 3600) > 1 ? 's' : ''} ago`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case '1':
        return { text: 'Success', class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' };
      case '2':
        return { text: 'Failed', class: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' };
      case '0':
      default:
        return { text: 'Pending', class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' };
    }
  };

  const refreshData = () => {
    fetchTransactions();
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
      <div className="flex flex-col flex-1">
        <Header />
        <main className="flex-1 p-6 overflow-auto">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
              >
                <ChevronLeftIcon className="h-5 w-5 mr-1" />
                Back
              </button>
              <h2 className="text-xl font-semibold">
                Live Transactions
              </h2>
              <div className="flex items-center">
                <div className="text-xs text-gray-500 dark:text-gray-400 mr-3">
                  {lastUpdateTime && `Updated ${formatTimeAgo(lastUpdateTime)}`}
                </div>
                <button
                  onClick={refreshData}
                  className="p-2 text-gray-600 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Transactions Table */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-x-auto">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h3 className="text-lg font-medium">Recent Transactions</h3>
                <div className="flex items-center">
                  <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">Live</span>
                </div>
              </div>

              {loading ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  Loading transactions...
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Number</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Operator</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
                    {transactions.length > 0 ? (
                      transactions.map((transaction) => {
                        const statusBadge = getStatusBadge(transaction.status);
                        return (
                          <tr key={transaction.order_id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                            <td className="px-4 py-3 text-sm dark:text-gray-300 text-blue-600 hover:text-blue-800"><a target='_blank' href={`order-details?orderId=${transaction.order_id}`}>{transaction.order_id}</a></td>
                            <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-300">{transaction.number}</td>
                            <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-300">
                              <div className="flex items-center">
                                <img
                                  src={transaction.operator_logo}
                                  alt={transaction.biller_name}
                                  className="h-6 w-6 mr-2 rounded-full"
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/24';
                                  }}
                                />
                                {transaction.biller_name}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">₹{transaction.amount}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusBadge.class}`}>
                                {statusBadge.text}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-300">
                              {formatDateTime(transaction.createAt)}
                            </td>
                            <td className="px-4 py-3 text-sm dark:text-gray-300 text-blue-600 hover:text-blue-800">
                              <a target='_blank' href={`profile?userId=${transaction.user_id}`}>{transaction.name}</a>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-sm text-gray-500">
                          No transactions found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </main>
      </div>

      <ToastContainer />
    </div>
  );
};

export default LiveTransaction;